import { push } from 'react-router-redux';
import { toast } from 'sonner';
import * as actionTypes from './actionTypes';
import { apiService, updateToken, refreshAccessToken, ensureCsrfToken } from '../../utils/api';
import history from '../../history';
import type { AppDispatch, RootState } from '../store';

export const profileStart = () => ({
  type: actionTypes.START_LOADING_USER_PROFILE,
});

export const authStart = () => ({
  type: actionTypes.AUTH_START,
});

export const profileSuccess = (data: any) => ({
  type: actionTypes.USER_PROFILE_LOADED,
  data,
});

export const authSuccess = (data: any) => ({
  type: actionTypes.AUTH_SUCCESS,
  data,
});

export const authFail = (error: any) => ({
  type: actionTypes.AUTH_FAIL,
  error,
});

export const logout = () => {
  localStorage.clear();
  return {
    type: actionTypes.AUTH_LOGOUT,
  };
};

export const logoutUser = () => async (dispatch: AppDispatch) => {
  try {
    await apiService.post('/my-rest-auth/logout/', {}, { silentError: true });
  } catch (_e) {
    console.log(_e);
  } finally {
    dispatch(logout());
    history.push('/login');
  }
};

export const checkAuthTimeout = (expirationTime: number) => (dispatch: AppDispatch) => {
  setTimeout(() => {
    dispatch(logout());
  }, expirationTime * 1000);
};

export const loadProfile = () => async (dispatch: AppDispatch) => {
  dispatch(profileStart());

  try {
    const { data } = await apiService.get(`/api/v1/user/employee-profiles/`);

    let currentEmployeeId = data?.current_employee?.id;

    // If no lastLogged profile, auto-select the first available employee
    if (!currentEmployeeId && Array.isArray(data?.employees) && data.employees.length > 0) {
      const firstEmployee = data.employees[0];
      data.current_employee = firstEmployee;
    }

    dispatch(profileSuccess(data));

    currentEmployeeId = data?.current_employee?.id;
    const employee = currentEmployeeId ? `?employee=${currentEmployeeId}` : '';
    const { data: menu } = await apiService.get(`/api/v1/user/menu-for-employee/${employee}`);
    if (menu) {
      dispatch({ type: 'UPDATE_MENU', menu });
    }
  } catch (err) {
    console.log(err);
  }
};

export const changeCurrentCompany =
  (id: string | number, company: string | number) => async (dispatch: AppDispatch) => {
    dispatch(currentCompanyChanged(id, company));
    dispatch({ type: 'START_FETCHING_MENU' });
    const menu = (
      await apiService.get(`/api/v1/user/menu-for-employee/`, {
        params: {
          employee: id,
        },
      })
    ).data;

    if (menu) {
      dispatch({ type: 'UPDATE_MENU', menu });
    }
  };

export const currentCompanyChanged = (id: string | number, company: string | number) => ({
  type: actionTypes.CHANGE_CURRENT_COMPANY,
  id,
  company,
});

export const authLogin =
  (username: string, password: string, intended?: string) => async (dispatch: AppDispatch) => {
    dispatch(authStart());

    try {
      delete apiService.defaults.headers.common['Authorization'];

      await ensureCsrfToken();
      const authData = (
        await apiService.post(
          '/my-rest-auth/login/',
          {
            email: username,
            password,
          },
          { silentError: true }
        )
      ).data;

      dispatch(authSuccess(authData));
      updateToken();
      (dispatch as any)(loadProfile());

      history.push(intended || '/app/akce');
    } catch (err) {
      toast.error('Chyba', { description: 'Špatné heslo nebo uživatelské jméno' });
      dispatch(authFail(err));
    }
  };

export const googleLogin =
  (credential: string, intended?: string) => async (dispatch: AppDispatch) => {
    dispatch(authStart());

    try {
      delete apiService.defaults.headers.common['Authorization'];
      await ensureCsrfToken();
      const authData = (
        await apiService.post(
          '/my-rest-auth/google-login/',
          {
            credential,
          },
          { silentError: true }
        )
      ).data;

      dispatch(authSuccess(authData));
      updateToken();
      (dispatch as any)(loadProfile());

      history.push(intended || '/app/akce');
    } catch (err) {
      toast.error('Chyba', { description: 'Přihlášení přes Google se nezdařilo' });
      dispatch(authFail(err));
    }
  };

export const refreshConnection = (access: string) => ({
  type: actionTypes.REFRESH_CONNECTION_SUCCESS,
  access,
});

export const register = (data: any) => async (dispatch: AppDispatch) => {
  dispatch({ type: actionTypes.REGISTRATION_START });
  delete apiService.defaults.headers.common.Authorization;

  try {
    const response = await apiService.post('/my-rest-auth/register/', data);

    dispatch({
      type: actionTypes.REGISTRATION_SUCC,
      data: response.data,
    });
  } catch (e) {
    dispatch({
      type: actionTypes.REGISTRATION_FAIL,
      data: e.response ? e.response.data : '',
    });
  }
};

export const confirmRegistration = (token: string) => async (dispatch: AppDispatch) => {
  delete apiService.defaults.headers.common.Authorization;
  try {
    await apiService.post(`/my-rest-auth/register/email-verify/`, {
      token,
    });

    dispatch({
      type: actionTypes.REGISTRATION_CONFIRM,
      data: null,
    });

    dispatch(push('/'));
  } catch (e) {
    dispatch({
      type: actionTypes.REGISTRATION_FAIL,
      data: e.response ? e.response.data : '',
    });
  }
};

const FALLBACK_COMPANY_ID = '00000000-0000-0000-0000-000000000000';

export const confirmEmployeeInvitation =
  (token: string, employeeId?: unknown) => async (dispatch: AppDispatch) => {
    delete apiService.defaults.headers.common.Authorization;
    // The backend ignores the company id in the URL (the company is resolved
    // internally from the token's employee_id); it only needs a syntactically
    // valid UUID for Django's URL routing.
    const companyId =
      typeof employeeId === 'string' && employeeId ? employeeId : FALLBACK_COMPANY_ID;
    try {
      const { data } = await apiService.post(
        `/api/v1/company/${companyId}/invite-employee-verify/`,
        {
          token,
        }
      );

      toast.success(data?.detail ?? 'Successfully invited');
      dispatch(push('/'));
    } catch {
      // The response interceptor already surfaces the error toast.
    }
  };

export const authCheckState = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  if (!getState().auth.isLoggedIn) {
    return;
  }

  try {
    await ensureCsrfToken();
    await refreshAccessToken();
    await (dispatch as any)(loadProfile());
  } catch (_e) {
    dispatch(logout());
  }
};

export const setPagePermission = (permission: any) => (dispatch: AppDispatch) => {
  dispatch({ type: actionTypes.SET_PAGE_PERMISSION, data: permission });
};
